/**
 * Parsing and formatting of the numbers typed into set tables. German input: comma or point as decimal mark.
 * Parsers return undefined for an empty field and NaN for text that is not a valid value. Durations are not typed
 * but set with a picker (`stepDuration`).
 */

export const MAX_WEIGHT_KG = 1000;
export const MAX_REPS = 9999;
export const MAX_DISTANCE_M = 100_000;

function parseDecimal(text: string): number | undefined {
  const s = text.trim().replace(",", ".");
  if (!s) return undefined;
  return /^\d+(\.\d*)?$|^\.\d+$/.test(s) ? Number(s) : NaN;
}

/** kg, up to two decimals (1.25 kg plates). */
export function parseWeight(text: string): number | undefined {
  const n = parseDecimal(text);
  if (n === undefined || Number.isNaN(n)) return n;
  return n <= MAX_WEIGHT_KG ? Math.round(n * 100) / 100 : NaN;
}

/** Whole reps. */
export function parseReps(text: string): number | undefined {
  const n = parseDecimal(text);
  if (n === undefined || Number.isNaN(n)) return n;
  return Number.isInteger(n) && n <= MAX_REPS ? n : NaN;
}

/** Metres, up to one decimal. */
export function parseDistance(text: string): number | undefined {
  const n = parseDecimal(text);
  if (n === undefined || Number.isNaN(n)) return n;
  return n <= MAX_DISTANCE_M ? Math.round(n * 10) / 10 : NaN;
}

/** A number for an input field: German decimal comma, no trailing zeros. */
export function formatNumber(n: number | undefined): string {
  return n === undefined || Number.isNaN(n) ? "" : String(n).replace(".", ",");
}

/** Seconds step of the time picker. */
export const DURATION_SECONDS_STEP = 5;
export const MAX_DURATION_MIN = 99;

/**
 * One tap on the time picker, which has a minutes and a seconds column like a clock: minutes go 0–99 and stop at
 * the ends, seconds go round in steps of 5 (55 → 0) without touching the minutes. Off-step seconds snap to the step.
 */
export function stepDuration(sec: number, part: "min" | "sec", direction: 1 | -1): number {
  const min = Math.floor(sec / 60);
  const s = sec % 60;
  if (part === "min") return Math.max(0, Math.min(MAX_DURATION_MIN, min + direction)) * 60 + s;
  const step = DURATION_SECONDS_STEP;
  const snapped = direction > 0 ? Math.floor(s / step) * step + step : Math.ceil(s / step) * step - step;
  return min * 60 + ((snapped % 60) + 60) % 60;
}
